/**
 * TradeHistoryService.js
 * Service for communicating with active strategy nodes to fetch trade history data
 */

import StrategyEngineService from './StrategyEngineService';

// Fetch real positions from Strategy Engine API
const fetchStrategyPositions = async () => {
  try {
    // Use the new endpoint that gets all positions directly from KV
    const allPositions = await StrategyEngineService.getAllPositions();
    return allPositions || [];
  } catch (error) {
    console.error('Error fetching strategy positions:', error);
    return [];
  }
};

// Fetch trade history using ONLY real strategy positions
const fetchTradeHistory = async (strategyId = null, dateRange = { start: null, end: null }) => {
  // Fetch real positions from Strategy Engine
  const strategyPositions = await fetchStrategyPositions();
  
  // Convert positions to trade format
  const realTrades = strategyPositions.map((pos, idx) => ({
    id: pos.id || `pos-${idx}`,
    date: pos.openedAt || new Date().toISOString(),
    timestamp: pos.openedAt || new Date().toISOString(),
    market: 'Crypto',
    symbol: pos.symbol || 'BTC-USDT',
    strategy: 'Strategy Builder',
    direction: pos.side || 'buy',
    size: pos.quantity || 0,
    entryPrice: pos.blendedEntryPrice || pos.averageEntryPrice || pos.entryPrice || 0,
    originalEntryPrice: pos.entryPrice || 0,
    exitPrice: pos.status === 'closed' ? (pos.exitPrice || pos.currentPrice) : pos.currentPrice,
    currentPrice: pos.status === 'closed' ? (pos.exitPrice || pos.currentPrice) : pos.currentPrice,
    // P&L values (net after fees) — use frozen finalPnl for closed positions
    pnl: pos.status === 'closed' ? (pos.finalPnl ?? pos.pnl ?? pos.netPnl ?? 0) : (pos.pnl || pos.netPnl || 0),
    pnlPercent: pos.status === 'closed' ? (pos.finalPnlPercent ?? pos.pnlPercent ?? pos.netPnlPercent ?? 0) : (pos.pnlPercent || pos.netPnlPercent || 0),
    // Gross P&L (before fees)
    grossPnl: pos.grossPnl || 0,
    grossPnlPercent: pos.grossPnlPercent || 0,
    // Net P&L (after fees) — use frozen values for closed positions
    netPnl: pos.status === 'closed' ? (pos.finalPnl ?? pos.netPnl ?? pos.pnl ?? 0) : (pos.netPnl || pos.pnl || 0),
    netPnlPercent: pos.status === 'closed' ? (pos.finalPnlPercent ?? pos.netPnlPercent ?? pos.pnlPercent ?? 0) : (pos.netPnlPercent || pos.pnlPercent || 0),
    duration: pos.openedAt ? calculateDuration(pos.openedAt, pos.closedAt) : '0h',
    closedAt: pos.closedAt || null,
    status: pos.status || 'open',  // 'open' | 'closed' | 'pending' | 'signal'
    // Trade type labeling
    orderType: pos.orderType || 'market',
    tradingMode: pos.tradingMode || 'spot',  // 'spot' | 'margin' | 'futures'
    leverage: pos.leverage || 1,
    broker: pos.broker || 'kucoin',
    // Fee tracking
    entryCommission: pos.entryCommission || 0,
    exitCommission: pos.exitCommission || 0,
    totalFees: pos.totalFees || 0,
    entryFeePercent: pos.entryFeePercent || 0.1,
    exitFeePercent: pos.exitFeePercent || 0.1,
    feeType: pos.feeType || 'taker',  // 'maker' | 'taker'
    limitPrice: pos.limitPrice,
    filledAt: pos.filledAt,
    runId: pos.runId,
    strategyId: pos.strategyId,
    isStrategyPosition: true,
    dynamicTakeProfit: pos.dynamicTakeProfit,
    dynamicStopLoss: pos.dynamicStopLoss,
    confidence: pos.confidence,
    lastRsi: pos.lastRsi,
    lastMacdSignal: pos.lastMacdSignal,
    lastAdjustment: pos.lastAdjustment,
    isAggregated: pos.isAggregated || false,
    tradeCount: pos.tradeCount || 1,
    logicalTrades: pos.logicalTrades || [],
    // Strategy info for hover tooltip
    strategyName: pos.strategyName || null,
    strategySnapshot: pos.strategySnapshot || null,
    // Mother/child grouping
    tradeGroupId: pos.tradeGroupId || null,
    // TP/SL auto toggles
    autoTpEnabled: pos.autoTpEnabled,
    autoSlEnabled: pos.autoSlEnabled,
    autoAiExitEnabled: pos.autoAiExitEnabled,
    // Paper/Real trading mode fields
    paperTrading: pos.paperTrading,
    isSimulated: pos.isSimulated,
    realMoney: pos.realMoney,
    // Manual budget adds
    manualAddsUsed: pos.manualAddsUsed || 0,
    // Loss/Win reports
    lossReport: pos.lossReport || null,
    winReport: pos.winReport || null,
    // Close reason
    closeReason: pos.closeReason || null,
    // Signal-specific fields (v4.4.3)
    signalExpiresAt: pos.signalExpiresAt || null,
    signalValidityMinutes: pos.signalValidityMinutes || null,
    signalAddedPrice: pos.signalAddedPrice || null,
    signalAddedAt: pos.signalAddedAt || null,
    method: pos.method || null,
  }));
  
  // Calculate real performance metrics from actual trades
  const performance = calculateRealPerformance(realTrades);
  const summary = calculateRealSummary(realTrades);
  
  // Get trade data from active strategy nodes
  const tradeHistoryData = {
    trades: realTrades,
    performance,
    summary,
    updatedAt: new Date().toISOString()
  };
  
  return tradeHistoryData;
};

// Calculate duration from open time (use closedAt for closed trades, now for open)
const calculateDuration = (openedAt, closedAt) => {
  const end = closedAt ? new Date(closedAt) : new Date();
  const opened = new Date(openedAt);
  const diffMs = end - opened;
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffMins = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
  
  if (diffHours > 24) {
    const days = Math.floor(diffHours / 24);
    return `${days}d ${diffHours % 24}h`;
  }
  return `${diffHours}h ${diffMins}m`;
};

// Calculate REAL performance metrics from actual trades
const calculateRealPerformance = (trades) => {
  if (!trades || trades.length === 0) {
    return {
      dailyData: [],
      metrics: {
        sharpeRatio: '0.00',
        maxDrawdown: '0.00%',
        profitFactor: '0.00',
        winRate: '0.00%',
        avgWin: '0.00%',
        avgLoss: '0.00%',
        expectancy: '0.00%'
      }
    };
  }
  
  // Calculate wins and losses from CLOSED trades only (not open positions with unrealized P&L)
  const closedTrades = trades.filter(t => t.status === 'closed');
  const wins = closedTrades.filter(t => parseFloat(t.pnl) > 0);
  const losses = closedTrades.filter(t => parseFloat(t.pnl) < 0);
  
  const totalPnl = closedTrades.reduce((sum, t) => sum + parseFloat(t.pnl || 0), 0);
  const winPnl = wins.reduce((sum, t) => sum + parseFloat(t.pnl || 0), 0);
  const lossPnl = Math.abs(losses.reduce((sum, t) => sum + parseFloat(t.pnl || 0), 0));
  
  const winRate = closedTrades.length > 0 ? (wins.length / closedTrades.length * 100) : 0;
  const avgWin = wins.length > 0 ? (winPnl / wins.length) : 0;
  const avgLoss = losses.length > 0 ? (lossPnl / losses.length) : 0;
  const profitFactor = lossPnl > 0 ? (winPnl / lossPnl) : winPnl > 0 ? 999 : 0;
  
  // Calculate expectancy
  const winProb = wins.length / (closedTrades.length || 1);
  const lossProb = losses.length / (closedTrades.length || 1);
  const expectancy = (winProb * avgWin) - (lossProb * avgLoss);
  
  // Calculate max drawdown from P&L sequence
  let peak = 0;
  let maxDrawdown = 0;
  let cumulative = 0;
  trades.forEach(t => {
    cumulative += parseFloat(t.pnl || 0);
    if (cumulative > peak) peak = cumulative;
    const drawdown = peak - cumulative;
    if (drawdown > maxDrawdown) maxDrawdown = drawdown;
  });
  const maxDrawdownPercent = peak > 0 ? (maxDrawdown / peak * 100) : 0;
  
  // Calculate Sharpe ratio (simplified - using daily returns)
  const returns = trades.map(t => parseFloat(t.pnlPercent || 0));
  const avgReturn = returns.reduce((a, b) => a + b, 0) / (returns.length || 1);
  const variance = returns.reduce((sum, r) => sum + Math.pow(r - avgReturn, 2), 0) / (returns.length || 1);
  const stdDev = Math.sqrt(variance);
  const sharpeRatio = stdDev > 0 ? (avgReturn / stdDev) : 0;
  
  // Generate daily data from trades
  const dailyData = generateDailyDataFromTrades(trades);
  
  return {
    dailyData,
    metrics: {
      sharpeRatio: sharpeRatio.toFixed(2),
      maxDrawdown: `-${maxDrawdownPercent.toFixed(2)}%`,
      profitFactor: profitFactor.toFixed(2),
      winRate: `${winRate.toFixed(2)}%`,
      avgWin: `${avgWin.toFixed(4)}`,
      avgLoss: `-${avgLoss.toFixed(4)}`,
      expectancy: `${expectancy.toFixed(4)}`
    }
  };
};

// Generate daily data from trades for charts
const generateDailyDataFromTrades = (trades) => {
  if (!trades || trades.length === 0) return [];
  
  // Group trades by date
  const tradesByDate = {};
  trades.forEach(t => {
    const date = new Date(t.date || t.timestamp).toISOString().split('T')[0];
    if (!tradesByDate[date]) tradesByDate[date] = [];
    tradesByDate[date].push(t);
  });
  
  // Calculate daily returns
  const dates = Object.keys(tradesByDate).sort();
  let cumulativeReturn = 0;
  
  return dates.map(date => {
    const dayTrades = tradesByDate[date];
    const dailyPnl = dayTrades.reduce((sum, t) => sum + parseFloat(t.pnlPercent || 0), 0);
    cumulativeReturn += dailyPnl;
    
    return {
      date,
      dailyReturn: dailyPnl.toFixed(2),
      cumulativeReturn: cumulativeReturn.toFixed(2)
    };
  });
};

// Calculate REAL summary from actual trades
const calculateRealSummary = (trades) => {
  if (!trades || trades.length === 0) {
    return {
      totalTrades: 0,
      openTrades: 0,
      activeStrategies: 0,
      bestPerforming: { strategy: 'N/A', return: '0.00%' },
      returns: { daily: '0.00%', weekly: '0.00%', monthly: '0.00%', yearly: '0.00%' },
      distribution: { crypto: '100%', stocks: '0%', forex: '0%', futures: '0%' }
    };
  }
  
  const openTrades = trades.filter(t => t.status === 'open');
  const closedTrades = trades.filter(t => t.status === 'closed');
  
  // Calculate total P&L
  const totalPnl = trades.reduce((sum, t) => sum + parseFloat(t.pnl || 0), 0);
  const totalPnlPercent = trades.reduce((sum, t) => sum + parseFloat(t.pnlPercent || 0), 0);
  
  // Calculate returns (simplified)
  const avgDailyReturn = trades.length > 0 ? totalPnlPercent / trades.length : 0;
  
  // Market distribution
  const cryptoTrades = trades.filter(t => t.market === 'Crypto').length;
  const cryptoPercent = trades.length > 0 ? (cryptoTrades / trades.length * 100) : 100;
  
  return {
    totalTrades: trades.length,
    openTrades: openTrades.length,
    activeStrategies: 1,
    bestPerforming: {
      strategy: 'Strategy Builder',
      return: `${totalPnlPercent.toFixed(2)}%`
    },
    returns: {
      daily: `${avgDailyReturn.toFixed(2)}%`,
      weekly: `${(avgDailyReturn * 7).toFixed(2)}%`,
      monthly: `${(avgDailyReturn * 30).toFixed(2)}%`,
      yearly: `${(avgDailyReturn * 365).toFixed(2)}%`
    },
    distribution: {
      crypto: `${cryptoPercent.toFixed(0)}%`,
      stocks: '0%',
      forex: '0%',
      futures: '0%'
    }
  };
};

// Mock data generators (kept for reference but not used)
const getMockTrades = (strategyId, dateRange) => {
  const now = new Date();
  const hashBase = strategyId ? strategyId.toString() : "default";
  const hash = hashBase.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  
  // Use the hash to make deterministic but varied results
  const tradeCount = 15 + (hash % 35); // 15-50 trades
  const trades = [];
  
  const markets = ['Stocks', 'Crypto', 'Forex', 'Futures'];
  const symbols = {
    'Stocks': ['AAPL', 'MSFT', 'GOOGL', 'AMZN', 'TSLA', 'META', 'NVDA', 'JPM', 'V', 'JNJ'],
    'Crypto': ['BTC', 'ETH', 'SOL', 'ADA', 'DOT', 'AVAX', 'MATIC', 'LINK', 'XRP', 'DOGE'],
    'Forex': ['EURUSD', 'GBPUSD', 'USDJPY', 'AUDUSD', 'USDCAD', 'USDCHF', 'NZDUSD', 'EURJPY', 'GBPJPY', 'EURGBP'],
    'Futures': ['ES', 'NQ', 'YM', 'RTY', 'CL', 'GC', 'SI', 'ZB', 'ZN', 'ZF']
  };
  
  const strategyNames = [
    'Mean Reversion', 'Trend Following', 'Breakout', 'Statistical Arbitrage', 
    'Moving Average Crossover', 'RSI Divergence', 'Sentiment Analysis', 
    'Machine Learning', 'Options Strategy', 'Grid Trading'
  ];
  
  // Deterministic strategy selection based on strategy ID
  const strategyIndex = strategyId ? hash % strategyNames.length : null;
  
  for (let i = 0; i < tradeCount; i++) {
    // Generate a trade date within the last 30 days
    const tradeDate = new Date(now);
    tradeDate.setDate(tradeDate.getDate() - (i % 30));
    
    // Skip if outside requested date range
    if (dateRange.start && new Date(dateRange.start) > tradeDate) continue;
    if (dateRange.end && new Date(dateRange.end) < tradeDate) continue;
    
    // Create deterministic but varied trade data
    const marketIndex = (hash + i) % markets.length;
    const market = markets[marketIndex];
    const symbolIndex = (hash + i * 3) % symbols[market].length;
    const symbol = symbols[market][symbolIndex];
    
    // Determine trade direction and result
    const isLong = ((hash + i) % 3) !== 0;
    const direction = isLong ? 'long' : 'short';
    
    // Calculate P&L with some randomness but biased by strategy ID
    let pnlFactor;
    if (strategyId) {
      // Strategy specific behavior
      pnlFactor = (((hash % 10) + i) % 10) / 10;
      if ((hash + i) % 7 === 0) {
        // Occasional big win
        pnlFactor = 0.8 + (hash % 10) / 50;
      } else if ((hash + i) % 11 === 0) {
        // Occasional big loss
        pnlFactor = -0.5 - (hash % 10) / 50;
      }
    } else {
      // More random behavior
      pnlFactor = ((hash + i * 7) % 20 - 8) / 10;
    }
    
    const entryPrice = 100 + (hash + i) % 900;
    const exitPrice = entryPrice * (1 + (isLong ? pnlFactor : -pnlFactor) / 10);
    const sizeFactor = (hash + i * 11) % 10 + 1;
    const size = sizeFactor * 100;
    
    // Calculate P&L
    const pnl = isLong ? 
      (exitPrice - entryPrice) * size / entryPrice : 
      (entryPrice - exitPrice) * size / entryPrice;
    
    const pnlPercent = (pnl / size) * 100;
    
    trades.push({
      id: `trade-${hash}-${i}`,
      date: tradeDate.toISOString(),
      market,
      symbol,
      strategy: strategyId ? strategyNames[strategyIndex] : strategyNames[(hash + i) % strategyNames.length],
      direction,
      size,
      entryPrice: entryPrice.toFixed(2),
      exitPrice: exitPrice.toFixed(2),
      pnl: pnl.toFixed(2),
      pnlPercent: pnlPercent.toFixed(2),
      status: new Date() - tradeDate < 24 * 60 * 60 * 1000 ? 'open' : 'closed',
      duration: ((hash + i * 5) % 48 + 1) + 'h',
      executedBy: 'Strategy Node'
    });
  }
  
  // Sort by date (most recent first)
  return trades.sort((a, b) => new Date(b.date) - new Date(a.date));
};

const getMockPerformance = (strategyId, dateRange) => {
  const hashBase = strategyId ? strategyId.toString() : "default";
  const hash = hashBase.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  
  // Generate daily performance data for last 30 days
  const now = new Date();
  const dailyData = [];
  let cumulativeReturn = 0;
  
  for (let i = 30; i >= 0; i--) {
    const date = new Date(now);
    date.setDate(date.getDate() - i);
    
    // Skip if outside requested date range
    if (dateRange.start && new Date(dateRange.start) > date) continue;
    if (dateRange.end && new Date(dateRange.end) < date) continue;
    
    // Daily return between -2% and +2% with a bias determined by strategy ID
    let dailyReturn;
    if (strategyId) {
      // Strategy specific behavior has some persistence and bias
      const bias = (hash % 10) / 100; // -0.05 to 0.04
      const noise = ((hash + i * 13) % 20 - 10) / 500; // -0.02 to 0.02
      dailyReturn = bias + noise;
      
      // Occasional outsized moves
      if ((hash + i) % 7 === 0) dailyReturn += 0.01;
      if ((hash + i) % 11 === 0) dailyReturn -= 0.01;
    } else {
      // More random behavior
      dailyReturn = ((hash + i * 7) % 40 - 20) / 1000;
    }
    
    cumulativeReturn += dailyReturn + cumulativeReturn * dailyReturn;
    
    dailyData.push({
      date: date.toISOString().split('T')[0],
      dailyReturn: (dailyReturn * 100).toFixed(2),
      cumulativeReturn: (cumulativeReturn * 100).toFixed(2)
    });
  }
  
  // Generate weekly metrics
  const wins = 3 + (hash % 5);
  const losses = 2 + (hash % 3);
  const total = wins + losses;
  const winRate = (wins / total * 100).toFixed(2);
  
  return {
    dailyData,
    metrics: {
      sharpeRatio: (0.5 + (hash % 15) / 10).toFixed(2),
      maxDrawdown: (-5 - (hash % 15)).toFixed(2) + '%',
      profitFactor: (1 + (hash % 20) / 10).toFixed(2),
      winRate: winRate + '%',
      avgWin: (((hash % 10) + 5) / 10).toFixed(2) + '%',
      avgLoss: (-((hash % 10) + 5) / 10).toFixed(2) + '%',
      expectancy: ((((hash % 10) + 5) / 10) * (wins / total) - (((hash % 10) + 5) / 10) * (losses / total)).toFixed(2) + '%'
    }
  };
};

const getMockSummary = (strategyId, realTradesCount = 0) => {
  const hashBase = strategyId ? strategyId.toString() : "default";
  const hash = hashBase.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  
  // Generate return based on performance
  const monthlyReturn = (hash % 20 - 5) / 100; // -0.05 to 0.15
  const yearlyReturn = monthlyReturn * 12 * (1 + (hash % 10) / 100); // Compound with some variance
  
  return {
    totalTrades: 15 + (hash % 35) + realTradesCount,
    openTrades: 2 + (hash % 5) + realTradesCount,
    activeStrategies: 1 + (hash % 3),
    bestPerforming: {
      strategy: 'Mean Reversion',
      return: (8 + (hash % 10)).toFixed(2) + '%'
    },
    returns: {
      daily: ((hash % 10 - 3) / 10).toFixed(2) + '%',
      weekly: ((hash % 15 - 5) / 10).toFixed(2) + '%',
      monthly: (monthlyReturn * 100).toFixed(2) + '%',
      yearly: (yearlyReturn * 100).toFixed(2) + '%'
    },
    distribution: {
      stocks: (30 + (hash % 20)).toFixed(2) + '%',
      crypto: (25 + (hash % 15)).toFixed(2) + '%',
      forex: (20 + (hash % 10)).toFixed(2) + '%',
      futures: (25 - (hash % 20)).toFixed(2) + '%'
    }
  };
};

// Service functions for the UI
const TradeHistoryService = {
  getTradeHistory: async (strategyId = null, dateRange = { start: null, end: null }) => {
    try {
      return await fetchTradeHistory(strategyId, dateRange);
    } catch (error) {
      console.error('Error fetching trade history data:', error);
      throw error;
    }
  },
  
  exportTradeData: async (strategyId, format = 'csv') => {
    try {
      // In a real implementation, this would generate and download the export
      await new Promise(resolve => setTimeout(resolve, 1000));
      return { success: true, message: `Trade data exported as ${format.toUpperCase()}` };
    } catch (error) {
      console.error('Error exporting trade data:', error);
      throw error;
    }
  },
  
  getActiveStrategies: async () => {
    try {
      // Fetch real strategies from the Strategy Engine API
      const strategies = await StrategyEngineService.listStrategies();
      return (strategies || []).map(s => ({
        id: s.id,
        name: s.name,
        status: s.status || 'active',
        market: 'Crypto',
      }));
    } catch (error) {
      console.error('Error fetching active strategies:', error);
      return [];
    }
  }
};

export default TradeHistoryService;
